/**
 * @file Async thunks for getting and patching data for detailed order state
 * @author Max van Loosbroek
 */

import { createAsyncThunk } from '@reduxjs/toolkit'
import { IHttpService } from 'angular'
import { getDetailed } from '../../../overview/Detailed/redux/thunks'
import { getOrder, patchTaasOrder, sendStatus } from './api'
import { UrlStatuses } from '../../types/types'
import {
  willAccept,
  willDecline,
  willShip,
  willDeliver,
  willAcceptReturn,
  willRejectReturn,
  willSetReturned,
  willAddTrackTrace,
  selectOrderDetailedState,
  stateStatusString
} from './selectors'
import { RootState } from '../../../../common/redux/root.reducer'
import moment from 'moment'
import { URL_STATUS } from '../../overview/constants'
import { URLS } from '../../../../common/utilities/constants/constants.module'
import { DetailedTaasOrder, DetailedTaasResponse, PatchTaas } from '../../../overview/types/types'
import { parseHttpError } from '../../../../common/utilities/errorHandling'

export const getOrderDetailed = createAsyncThunk<
DetailedTaasOrder,
{ orderId?: number },
{ extra: { $http: IHttpService } }
>('ordersDetailed/getOrderDetailed', async ({ orderId }, thunkApi) => {
  // Get order
  const { data: order } = await getOrder(thunkApi.extra.$http, orderId)
  await thunkApi.dispatch(
    getDetailed({
      taasId: order.taasId
    })
  )
  return order
})

export const updateDetailedOrder = createAsyncThunk<
DetailedTaasOrder,
void,
{ extra: { $http: IHttpService } }
>('ordersDetailed/updateDetailedOrder', async (_, thunkApi): Promise<DetailedTaasOrder> => {
  const state: RootState = thunkApi.getState() as any
  const orderDetailed = selectOrderDetailedState(state)
  const orderId = orderDetailed.originalOrder?.id
  const shippedAt = orderDetailed.order?.status?.shippedAt
  const taasId = state.ordersDetailed?.order?.taasId
  const declineReason = state.ordersDetailed?.order?.declineReason
  const returnRejectReason = state.ordersDetailed?.order?.returnRejectReason
  if (
    willAccept(state) ||
    willDecline(state) ||
    willDeliver(state) ||
    willAcceptReturn(state) ||
    willRejectReturn(state)
  ) {
    const urlStatus = URL_STATUS[stateStatusString(state) as UrlStatuses]
    const body = declineReason ? { declineReason } : { returnRejectReason }
    const { data } = await sendStatus(thunkApi.extra.$http, orderId, urlStatus, body)
    await thunkApi.dispatch(
      getDetailed({
        taasId: data.taasId,
        serialId: data.dashboardRobot?.id
      })
    )
    return data
  } else if (willShip(state)) {
    const startAt = moment(shippedAt)
      .add(1, 'days')
      .toISOString()

    const taasUrl = `${URLS.admin.taas}/${String(taasId)}`
    const requestBody: PatchTaas = { shippedAt, startAt }
    await thunkApi.extra.$http.patch<DetailedTaasResponse>(taasUrl, requestBody).catch(parseHttpError)

    await thunkApi.dispatch(
      getDetailed({
        taasId
      })
    )
  } else if (willSetReturned(state)) {
    // patch taas
    const endAt = state.detailed.taas.endAt

    const taasUrl = `${URLS.admin.taas}/${taasId}`
    const requestBody: PatchTaas = { endAt }
    await thunkApi.extra.$http
      .patch<DetailedTaasResponse>(taasUrl, requestBody)
      .catch(parseHttpError)

    await thunkApi.dispatch(
      getDetailed({
        taasId
      })
    )
  }
  if (willAddTrackTrace(state)) {
    const trackTraceCode = state.ordersDetailed?.order?.trackTraceCode

    const { data } = await patchTaasOrder(thunkApi.extra.$http, orderId, { trackTraceCode })
    return data
  }
  if (state.ordersDetailed?.order?.internalNotes !== state.ordersDetailed.originalOrder?.internalNotes) {
    const internalNotes = state.ordersDetailed?.order?.internalNotes

    const { data } = await patchTaasOrder(thunkApi.extra.$http, orderId, { internalNotes })
    return data
  }
  return await getOrder(thunkApi.extra.$http, orderId).then(res => res.data)
})
